import { useReducer, useMemo } from 'react';
import { INPUT_STATE, isNullOperator } from '../GlobalSearchModal.constants';
import { getFilterConfig } from '../GlobalSearchModal.filterConfig';

const DEFAULT_PAGE_SIZE = 30;

const initialState = {
    // Input & tokens
    searchQuery: '',
    searchTokens: [],
    currentFilter: null,
    currentOperator: null,
    filterInputValue: null,
    
    searchResults: [],
    totalCount: 0,
    hasMore: false,
    
    status: 'idle', // 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error'
    
    isInputFocused: false,
    highlightedIndex: -1,
};

// Derive input state from filter/operator
const deriveInputState = (currentFilter, currentOperator) => {
    if (!currentFilter) return INPUT_STATE.FILTER;
    const filterConfig = getFilterConfig(currentFilter.key);
    if (!currentOperator && !filterConfig?.skipOperator) return INPUT_STATE.OPERATOR;
    return INPUT_STATE.VALUE;
};

const actionTypes = {
    SET_QUERY: 'SET_QUERY',
    SELECT_FILTER: 'SELECT_FILTER',
    SELECT_OPERATOR: 'SELECT_OPERATOR',
    ADD_TOKEN: 'ADD_TOKEN',
    REMOVE_TOKEN: 'REMOVE_TOKEN',
    GO_BACK: 'GO_BACK',
    RESET: 'RESET',
    SET_FILTER_INPUT_VALUE: 'SET_FILTER_INPUT_VALUE',
    CANCEL_FILTER: 'CANCEL_FILTER',
    SET_STATUS: 'SET_STATUS',
    SET_RESULTS: 'SET_RESULTS',
    APPEND_RESULTS: 'APPEND_RESULTS',
    LOAD_RECENT: 'LOAD_RECENT',
    SET_INPUT_FOCUS: 'SET_INPUT_FOCUS',
    SET_HIGHLIGHTED_INDEX: 'SET_HIGHLIGHTED_INDEX',
};

const reducer = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_QUERY:
            // Reset highlighted index when query changes
            return { ...state, searchQuery: action.payload, highlightedIndex: -1 };
            
        case actionTypes.SELECT_FILTER: {
            const { filter, skipOperator } = action.payload;
            return {
                ...state,
                currentFilter: filter,
                currentOperator: skipOperator ? '=' : null,
                searchQuery: '',
                filterInputValue: null,
                highlightedIndex: -1,
            };
        }
            
        case actionTypes.SELECT_OPERATOR: {
            const { operator, isNullOperator, filter } = action.payload;
            // Use filter from payload if provided, otherwise use state.currentFilter
            const filterToUse = filter || state.currentFilter;
            if (isNullOperator) {
                return {
                    ...state,
                    searchTokens: [...state.searchTokens, {
                        filter: filterToUse,
                        operator,
                        value: null,
                    }],
                    currentFilter: null,
                    currentOperator: null,
                    searchQuery: '',
                    highlightedIndex: -1,
                };
            }
            return { ...state, currentOperator: operator, searchQuery: '', highlightedIndex: -1 };
        }
            
        case actionTypes.ADD_TOKEN:
            return {
                ...state,
                searchTokens: [...state.searchTokens, action.payload],
                currentFilter: null,
                currentOperator: null,
                searchQuery: '',
                filterInputValue: null,
                highlightedIndex: -1,
            };
            
        case actionTypes.REMOVE_TOKEN: {
            const newTokens = state.searchTokens.filter((_, i) => i !== action.payload);
            return newTokens.length === 0 
                ? { ...initialState }
                : { ...state, searchTokens: newTokens, currentFilter: null, currentOperator: null, highlightedIndex: -1 };
        }
            
        case actionTypes.GO_BACK: {
            const inputState = deriveInputState(state.currentFilter, state.currentOperator);
            if (inputState === INPUT_STATE.VALUE) {
                const filterConfig = state.currentFilter ? getFilterConfig(state.currentFilter.key) : null;
                if (filterConfig?.skipOperator) {
                    return { ...state, currentFilter: null, currentOperator: null, searchQuery: '', filterInputValue: null, highlightedIndex: -1 };
                }
                return { ...state, currentOperator: null, searchQuery: '', highlightedIndex: -1 };
            }
            if (inputState === INPUT_STATE.OPERATOR) {
                return { ...state, currentFilter: null, searchQuery: '', highlightedIndex: -1 };
            }
            return state;
        }
            
        case actionTypes.RESET:
            return { ...initialState };
            
        case actionTypes.SET_FILTER_INPUT_VALUE:
            return { ...state, filterInputValue: action.payload };
            
        case actionTypes.CANCEL_FILTER:
            return { ...state, filterInputValue: null, currentFilter: null, currentOperator: null, highlightedIndex: -1 };
            
        case actionTypes.SET_STATUS:
            return { ...state, status: action.payload };
            
        case actionTypes.SET_RESULTS:
            return {
                ...state,
                searchResults: action.payload.results,
                totalCount: action.payload.totalCount,
                hasMore: action.payload.hasMore,
                status: 'ready',
            };
            
        case actionTypes.APPEND_RESULTS:
            return {
                ...state,
                searchResults: [...state.searchResults, ...action.payload.results],
                totalCount: action.payload.totalCount,
                hasMore: action.payload.hasMore,
                status: 'ready',
            };
            
        case actionTypes.LOAD_RECENT:
            return { ...state, searchTokens: action.payload, searchQuery: '', highlightedIndex: -1 };
            
        case actionTypes.SET_INPUT_FOCUS:
            return { ...state, isInputFocused: action.payload };
            
        case actionTypes.SET_HIGHLIGHTED_INDEX:
            return { ...state, highlightedIndex: action.payload };
            
        default:
            return state;
    }
};

export const useSearchState = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    
    // Derived states
    const currentInputState = useMemo(
        () => deriveInputState(state.currentFilter, state.currentOperator),
        [state.currentFilter, state.currentOperator]
    );
    
    const showFilterInput = useMemo(() => {
        if (!state.currentFilter) return false;
        const filterConfig = getFilterConfig(state.currentFilter.key);
        return filterConfig?.isDateRange === true;
    }, [state.currentFilter]);
    
    // hasSearched should be true during loadingMore too (we have results, just loading more)
    const hasSearched = state.status === 'ready' || state.status === 'error' || state.status === 'loadingMore';
    const isLoading = state.status === 'loading';
    const isLoadingMore = state.status === 'loadingMore';
    
    // Actions
    const actions = useMemo(() => ({
        setQuery: (query) => dispatch({ type: actionTypes.SET_QUERY, payload: query }),
        
        selectFilter: (filter) => {
            const filterConfig = getFilterConfig(filter.key);
            dispatch({ type: actionTypes.SELECT_FILTER, payload: { filter, skipOperator: filterConfig?.skipOperator } });
        },
        
        selectOperator: (operator, filter = null) => {
            const isNullOp = isNullOperator(operator);
            dispatch({ type: actionTypes.SELECT_OPERATOR, payload: { operator, isNullOperator: isNullOp, filter } });
        },
        
        addToken: (filter, operator, value) => {
            dispatch({ type: actionTypes.ADD_TOKEN, payload: { filter, operator, value } });
        },
        
        addFreeTextToken: (value) => {
            dispatch({ type: actionTypes.ADD_TOKEN, payload: { filter: null, operator: null, value } });
        },
        
        removeToken: (index) => dispatch({ type: actionTypes.REMOVE_TOKEN, payload: index }),
        goBack: () => dispatch({ type: actionTypes.GO_BACK }),
        reset: () => dispatch({ type: actionTypes.RESET }),
        setFilterInput: (value) => dispatch({ type: actionTypes.SET_FILTER_INPUT_VALUE, payload: value }),
        cancelFilter: () => dispatch({ type: actionTypes.CANCEL_FILTER }),
        loadRecent: (tokens) => dispatch({ type: actionTypes.LOAD_RECENT, payload: tokens }),
        setInputFocus: (focused) => dispatch({ type: actionTypes.SET_INPUT_FOCUS, payload: focused }),
        setHighlightedIndex: (index) => dispatch({ type: actionTypes.SET_HIGHLIGHTED_INDEX, payload: index }),
        
        // Search actions
        setLoading: () => dispatch({ type: actionTypes.SET_STATUS, payload: 'loading' }),
        setLoadingMore: () => dispatch({ type: actionTypes.SET_STATUS, payload: 'loadingMore' }),
        setError: () => dispatch({ type: actionTypes.SET_STATUS, payload: 'error' }),
        setResults: (results, totalCount, hasMore) => {
            dispatch({ type: actionTypes.SET_RESULTS, payload: { results, totalCount, hasMore } });
        },
        appendResults: (results, totalCount, hasMore) => {
            dispatch({ type: actionTypes.APPEND_RESULTS, payload: { results, totalCount, hasMore } });
        },
    }), []);
    
    return {
        state: {
            ...state,
            currentInputState,
            showFilterInput,
            hasSearched,
            isLoading,
            isLoadingMore,
        },
        actions,
        constants: { DEFAULT_PAGE_SIZE },
    };
};
